<script lang="ts" setup>
import { computed, ref } from 'vue';

import type { AppRadioButtonOption } from './AppRadioButtons.model';

interface AppRadioButtonsProps {
  items: AppRadioButtonOption[];
  itemValue?: number;
  // NOTE:
  // New desktop version uses different color scheme for these buttons,
  // hence this props is here until we will be able to remove old code flow.
  theme: 'old' | 'new';
}

const props = defineProps<AppRadioButtonsProps>();

interface AppRadioButtonsEmits {
  (event: 'toggleSelected', value?: number): void;
}

const emit = defineEmits<AppRadioButtonsEmits>();

const customRadioButtonsSwitch = ref<HTMLDivElement>();
const dynamicWidth = ref<number>();

const radioButtonWidth = computed(() => {
  if (!customRadioButtonsSwitch.value) {
    return;
  }
  const width = getRadioButtonWidth(
    customRadioButtonsSwitch.value.clientWidth,
    props.items.length
  );

  return width;
});

const indicatorWidthStyle = computed(() => {
  const offset = 5;

  if (dynamicWidth.value) {
    return `${dynamicWidth.value - offset}px`;
  }

  if (radioButtonWidth.value) {
    return `${radioButtonWidth.value - offset}px`;
  }

  return '';
});

function getMargin(itemValue: number | undefined): number {
  const topRightItemMargin = 0;
  const topLeftMargin = 5;
  const centerItemMargin = 2.5;

  if (itemValue === undefined) {
    return topLeftMargin;
  } else if (itemValue === 0) {
    return centerItemMargin;
  }
  return topRightItemMargin;
}

function setWidth(): void {
  if (!customRadioButtonsSwitch.value) {
    return;
  }
  dynamicWidth.value = getRadioButtonWidth(
    customRadioButtonsSwitch.value?.clientWidth,
    props.items.length
  );
}

function toggleTransform(id: number | undefined): string {
  if (!radioButtonWidth.value) {
    return '';
  }
  if (id === 0) {
    if (dynamicWidth.value) {
      const width = dynamicWidth.value;
      return `translateX(${width}px)`;
    }
    const width = radioButtonWidth.value;
    return `translateX(${width}px)`;
  }
  if (id === 1) {
    if (dynamicWidth.value) {
      const width = dynamicWidth.value * 2;
      return `translateX(${width}px)`;
    }
    const width = radioButtonWidth.value * 2;
    return `translateX(${width}px)`;
  }
  return 'translateX(0px)';
}

function getRadioButtonWidth(
  containerWidth: number,
  itemLength: number
): number {
  return containerWidth / itemLength;
}
</script>

<template>
  <div
    ref="customRadioButtonsSwitch"
    v-resize:clientWidth="setWidth"
    class="app-radio-buttons"
    :class="[props.theme]"
  >
    <template v-for="item in items" :key="item.id">
      <input :id="item.id" name="switch" type="radio" />
      <label
        t-id="AppRadioButtons-radioBtn"
        :for="item.id"
        class="label"
        :class="{ active: item.active === itemValue }"
        :style="{
          width: dynamicWidth ? `${dynamicWidth}%` : `${radioButtonWidth}%`,
        }"
        @click="emit('toggleSelected', item.active)"
      >
        {{ item.label }}
      </label>
    </template>
    <div
      class="indicator"
      :style="{
        width: indicatorWidthStyle,
        left: `${getMargin(itemValue)}px`,
        transform: toggleTransform(itemValue),
      }"
    />
  </div>
</template>

<style scoped lang="scss">
@import '../../styles/_fonts.scss';
.app-radio-buttons {
  position: relative;
  display: flex;
  height: 50px;
  justify-content: space-around;
  background-color: var(--color-white);
  border-radius: var(--border-radius-12);

  &.new {
    background-color: var(--color-secondary-95);
  }

  .label {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--color-secondary-60);
    @include font-label-l-light;

    &.active {
      @include font-label-l-bold;
      color: var(--color-white);
    }
  }

  .indicator {
    height: 40px;
    position: absolute;
    top: 5px;
    background: var(--color-primary-20);
    border-radius: var(--border-radius-8);
    transition: transform 600ms cubic-bezier(0.02, 0.94, 0.09, 0.97),
      background 300ms cubic-bezier(0.17, 0.67, 0.14, 1.03);
  }

  input {
    display: none;
  }
}
</style>
